<template>
  <Dialog
    :visible="true"
    modal
    header="Complete your profile"
    :style="{ width: '28rem' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    :closable="false"
    :close-on-escape="false"
  >
    <!-- The Save button in the footer submits this form, so Enter saves -->
    <form
      id="required-name-form"
      class="kb-form"
      novalidate
      @submit.prevent="onSave"
    >
      <p class="kb-dialog-lead">
        Please provide your first and last name to continue using KreWeBacchus.
      </p>

      <div class="kb-form-pair">
        <KbField
          v-slot="{ describedBy }"
          field-id="required-first-name"
          label="First name"
          :error="firstNameError"
        >
          <InputText
            id="required-first-name"
            v-model="firstName"
            autocomplete="given-name"
            size="small"
            fluid
            placeholder="Enter your first name"
            :autofocus="!focusLastName"
            :invalid="Boolean(firstNameError)"
            :aria-describedby="describedBy"
          />
        </KbField>

        <KbField
          v-slot="{ describedBy }"
          field-id="required-last-name"
          label="Last name"
          :error="lastNameError"
        >
          <InputText
            id="required-last-name"
            v-model="lastName"
            autocomplete="family-name"
            size="small"
            fluid
            placeholder="Enter your last name"
            :autofocus="focusLastName"
            :invalid="Boolean(lastNameError)"
            :aria-describedby="describedBy"
          />
        </KbField>
      </div>
      <p
        v-if="saveError"
        class="kb-callout-alert m-0"
        role="alert"
      >
        {{ saveError }}
      </p>
    </form>

    <template #footer>
      <Button
        type="submit"
        form="required-name-form"
        label="Save"
        size="small"
        :loading="saving"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

const props = defineProps<{
  member: Member
}>()

const membersStore = useMembersStore()

const saving = ref(false)
const submitted = ref(false)
const saveError = ref('')

// Pre-fill with existing data if available
const firstName = ref(props.member.first_name || '')
const lastName = ref(props.member.last_name || '')

// If only full name exists, try to parse it
if (!firstName.value && !lastName.value && props.member.name) {
  const parts = props.member.name.trim().split(/\s+/)
  const [parsedFirst, ...parsedRest] = parts
  if (parsedFirst && parsedRest.length > 0) {
    firstName.value = parsedFirst
    lastName.value = parsedRest.join(' ')
  }
  else if (parsedFirst) {
    firstName.value = parsedFirst
  }
}

// Focus starts in the first empty field (first name when both are empty or
// both are filled)
const focusLastName = Boolean(firstName.value.trim()) && !lastName.value.trim()

const firstNameError = computed(() =>
  submitted.value && !firstName.value.trim() ? 'First name is required' : undefined)
const lastNameError = computed(() =>
  submitted.value && !lastName.value.trim() ? 'Last name is required' : undefined)

const onSave = async () => {
  if (saving.value) return
  submitted.value = true

  if (!firstName.value.trim() || !lastName.value.trim()) {
    return
  }

  saving.value = true
  try {
    const fullName = `${firstName.value.trim()} ${lastName.value.trim()}`
    saveError.value = ''
    const saved = await membersStore.updateMember(props.member, {
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      name: fullName,
    })
    if (!saved) {
      saveError.value = 'Your details weren\'t saved. Please try again.'
    }
    // Dialog will auto-close when memberNeedsNameUpdate becomes false
  }
  finally {
    saving.value = false
  }
}
</script>
